/**
 * Prozedurale Sound-Effekte über die Web-Audio-API — keine Asset-Dateien.
 * Alle Klänge werden aus Oszillatoren + gefiltertem Rauschen synthetisiert.
 *
 * Hinweis: das hier verwendete Math.random() erzeugt ausschließlich AUDIO-Rauschen
 * (Würfelrasseln, Bust-Donner). Spiel-Zufall läuft serverseitig über crypto.randomInt.
 *
 * AudioContext startet erst nach einer Nutzer-Geste (Autoplay-Policy) — wir
 * lazy-initialisieren beim ersten Abspielen und resumen den Context.
 */

const MUTE_KEY = 'dice-muted';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = readMuted();

function readMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

export function isMuted(): boolean { return muted; }

export function toggleMute(): boolean {
  muted = !muted;
  try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* ignore */ }
  if (!muted) { ac(); chime(); } // kurzes Feedback beim Einschalten
  return muted;
}

function ac(): AudioContext | null {
  if (muted) return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0.42;
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

type ToneOpts = {
  freq: number;
  type?: OscillatorType;
  dur: number;
  gain?: number;
  attack?: number;
  when?: number;
  slideTo?: number;
};

function tone(o: ToneOpts): void {
  const c = ac();
  if (!c || !master) return;
  const t0 = c.currentTime + (o.when ?? 0);
  const osc = c.createOscillator();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(o.freq, t0);
  if (o.slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.slideTo), t0 + o.dur);
  const g = c.createGain();
  const peak = o.gain ?? 0.2;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + (o.attack ?? 0.005));
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);
  osc.connect(g).connect(master);
  osc.start(t0);
  osc.stop(t0 + o.dur + 0.03);
}

function noiseBurst(o: { dur: number; gain: number; when?: number; type?: BiquadFilterType; freq?: number; q?: number }): void {
  const c = ac();
  if (!c || !master) return;
  const t0 = c.currentTime + (o.when ?? 0);
  const len = Math.max(1, Math.floor(c.sampleRate * o.dur));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1; // cosmetic audio noise only
  const src = c.createBufferSource();
  src.buffer = buf;
  const filt = c.createBiquadFilter();
  filt.type = o.type ?? 'bandpass';
  filt.frequency.value = o.freq ?? 1200;
  filt.Q.value = o.q ?? 1;
  const g = c.createGain();
  g.gain.setValueAtTime(o.gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);
  src.connect(filt).connect(g).connect(master);
  src.start(t0);
  src.stop(t0 + o.dur + 0.03);
}

function chime(): void { tone({ freq: 880, type: 'sine', dur: 0.18, gain: 0.18 }); }

// --- Spiel-Events ---

/** Würfelbecher schütteln + Würfel fallen: gestaffelte Rausch-Klacks + tiefer Aufprall. */
export function playRoll(): void {
  const clacks = [0, 0.05, 0.1, 0.15, 0.19];
  for (const w of clacks) {
    noiseBurst({ dur: 0.05, gain: 0.18, when: w, type: 'bandpass', freq: 900 + Math.random() * 900, q: 1.2 });
  }
  noiseBurst({ dur: 0.12, gain: 0.12, when: 0.2, type: 'lowpass', freq: 320, q: 0.7 });
}

/** Würfel beiseitelegen — leiser Klack. */
export function playSelect(): void {
  tone({ freq: 420, type: 'triangle', dur: 0.06, gain: 0.1 });
  noiseBurst({ dur: 0.03, gain: 0.06, type: 'highpass', freq: 2600 });
}

/** Hot Dice — heller aufsteigender Schimmer. */
export function playHotDice(): void {
  [523, 659, 880, 1175].forEach((f, i) => tone({ freq: f, type: 'triangle', dur: 0.35, gain: 0.16, when: i * 0.07 }));
}

/** Bust — absackender Donner. */
export function playBust(): void {
  tone({ freq: 240, type: 'sawtooth', dur: 0.55, gain: 0.22, slideTo: 55 });
  noiseBurst({ dur: 0.5, gain: 0.18, type: 'lowpass', freq: 260, q: 0.6 });
}

/** Banken — eingesackte Münzen, warmer Akkord-Ping. */
export function playBank(): void {
  [660, 880, 1320].forEach((f, i) => tone({ freq: f, type: 'sine', dur: 0.5, gain: 0.16, when: i * 0.04 }));
  noiseBurst({ dur: 0.06, gain: 0.05, when: 0, type: 'highpass', freq: 4000 });
}

/** Sieg — kurze Fanfare. */
export function playWin(): void {
  [523, 659, 784, 1047].forEach((f, i) => tone({ freq: f, type: 'triangle', dur: 0.4, gain: 0.2, when: i * 0.12 }));
  tone({ freq: 1568, type: 'sine', dur: 0.6, gain: 0.16, when: 0.48 });
}

/** „Du bist dran" — sanfter Zwei-Ton-Gong. */
export function playYourTurn(): void {
  tone({ freq: 784, type: 'sine', dur: 0.3, gain: 0.18 });
  tone({ freq: 1175, type: 'sine', dur: 0.4, gain: 0.15, when: 0.12 });
}
